import React, { useEffect, useState } from "react";
import { api, formatCHF, currentMonthString, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  fahrer_id: "",
  fahrzeug_id: "",
  stopps: 0,
  umsatz_pro_stopp: 14.50,
  fahrerlohn_pro_stopp: 10.00,
  bemerkung: "",
  status: "offen",
};

export default function Aldi() {
  const [monat, setMonat] = useState(currentMonthString());
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const [r, e, v] = await Promise.all([
      api.get("/aldi-tours", { params: { monat } }),
      api.get("/employees"),
      api.get("/vehicles"),
    ]);
    setList(r.data); setEmployees(e.data); setVehicles(v.data);
  };
  useEffect(() => { load(); }, [monat]);

  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const vehName = (id) => vehicles.find((v) => v.id === id)?.kennzeichen || "—";

  const umsatz = (Number(form.stopps) || 0) * (Number(form.umsatz_pro_stopp) || 0);
  const fLohn = (Number(form.stopps) || 0) * (Number(form.fahrerlohn_pro_stopp) || 0);

  const save = async () => {
    try {
      const payload = {
        ...form,
        stopps: Number(form.stopps) || 0,
        umsatz_pro_stopp: Number(form.umsatz_pro_stopp) || 0,
        fahrerlohn_pro_stopp: Number(form.fahrerlohn_pro_stopp) || 0,
        fahrzeug_id: form.fahrzeug_id || null,
        umsatz_total: null,
        fahrerlohn_total: null,
      };
      if (editing) await api.put(`/aldi-tours/${editing.id}`, payload);
      else await api.post("/aldi-tours", payload);
      toast.success("Gespeichert");
      setOpen(false); load();
    } catch { toast.error("Fehler"); }
  };

  return (
    <PageContainer>
      <PageHeader title="Aldi-Touren" subtitle="Stopps-basierte Abrechnung" testId="aldi-page">
        <MonthPicker value={monat} onChange={setMonat} testId="aldi-month-picker" />
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="add-aldi-button">
          <Plus className="h-4 w-4 mr-2" /> Neue Aldi-Tour
        </Button>
      </PageHeader>

      <Card className="border-neutral-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead><TableHead>Fahrer</TableHead><TableHead>Fahrzeug</TableHead>
                <TableHead className="text-right">Stopps</TableHead><TableHead className="text-right">Umsatz</TableHead>
                <TableHead className="text-right">Fahrerlohn</TableHead><TableHead className="text-right">Gewinn</TableHead>
                <TableHead>Status</TableHead><TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 && (<TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-8">Keine Aldi-Touren.</TableCell></TableRow>)}
              {list.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono">{formatDatum(a.datum)}</TableCell>
                  <TableCell>{empName(a.fahrer_id)}</TableCell>
                  <TableCell className="font-mono">{vehName(a.fahrzeug_id)}</TableCell>
                  <TableCell className="nivara-num">{a.stopps}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(a.umsatz_total)}</TableCell>
                  <TableCell className="nivara-num">{formatCHF(a.fahrerlohn_total)}</TableCell>
                  <TableCell className="nivara-num text-[#008A00] font-medium">{formatCHF((a.umsatz_total||0)-(a.fahrerlohn_total||0))}</TableCell>
                  <TableCell><Badge className={a.status === "abgerechnet" ? "bg-[#008A00] text-white" : a.status === "geprüft" ? "bg-[#0055FF] text-white" : "bg-[#FFB800] text-neutral-900"}>{a.status}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(a); setForm({ ...EMPTY, ...a, fahrzeug_id: a.fahrzeug_id || "" }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={async () => { if(window.confirm("Löschen?")){ await api.delete(`/aldi-tours/${a.id}`); load();}}}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl" data-testid="aldi-dialog">
          <DialogHeader><DialogTitle>{editing ? "Aldi-Tour bearbeiten" : "Neue Aldi-Tour"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Datum</Label><Input type="date" value={form.datum} onChange={(e) => setForm({...form, datum: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Anzahl Stopps</Label><Input type="number" step="1" value={form.stopps} onChange={(e) => setForm({...form, stopps: e.target.value})} className="font-mono" data-testid="aldi-stopps-input" /></div>
            <div className="space-y-2">
              <Label>Fahrer</Label>
              <Select value={form.fahrer_id} onValueChange={(v) => setForm({...form, fahrer_id: v})}>
                <SelectTrigger><SelectValue placeholder="Fahrer wählen…" /></SelectTrigger>
                <SelectContent>{employees.map((e) => (<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Fahrzeug</Label>
              <Select value={form.fahrzeug_id || "_none"} onValueChange={(v) => setForm({...form, fahrzeug_id: v === "_none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent><SelectItem value="_none">— Kein Fahrzeug —</SelectItem>{vehicles.map((v) => (<SelectItem key={v.id} value={v.id}>{v.kennzeichen}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Umsatz pro Stopp (CHF)</Label><Input type="number" step="0.01" value={form.umsatz_pro_stopp} onChange={(e) => setForm({...form, umsatz_pro_stopp: e.target.value})} className="font-mono" /></div>
            <div className="space-y-2"><Label>Fahrerlohn pro Stopp (CHF)</Label><Input type="number" step="0.01" value={form.fahrerlohn_pro_stopp} onChange={(e) => setForm({...form, fahrerlohn_pro_stopp: e.target.value})} className="font-mono" /></div>
            <Card className="sm:col-span-2 bg-neutral-50 p-3 border-neutral-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><div className="text-xs text-neutral-500 uppercase tracking-wider">Umsatz Total</div><div className="font-mono font-semibold text-lg">{formatCHF(umsatz)}</div></div>
                <div><div className="text-xs text-neutral-500 uppercase tracking-wider">Fahrerlohn Total</div><div className="font-mono font-semibold text-lg">{formatCHF(fLohn)}</div></div>
              </div>
            </Card>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["offen","geprüft","abgerechnet"].map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2"><Label>Bemerkung</Label><Input value={form.bemerkung || ""} onChange={(e) => setForm({...form, bemerkung: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button onClick={save} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="aldi-save">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
